import { BROADSIGN_UPDATE_CAMPAIGN_BY_DEAL } from './broadSignUpdateCampaignByDeal'
import { UpdateCampaignByDealModel } from '../../pages/Campaign/CampaignTable/BroadSignAddModal/models/update-campaign-by-deal.model'
import { BroadSignFiltersModel } from './models/broad-sign-filters.model'
import { FetchResult } from '@apollo/client'
import { BroadSignCreationsResponseModel } from './models/broad-sign-creations-response.model'
import { BROADSIGN_CREATIONS } from './broadSignCreations'
import { client } from '../../components/ApolloProvider'
import { BroadSignUpdateCampaignByDealResponse, SortDirection } from '../../types/various'
import { BROADSIGN_CREATIONS_PER_PAGE } from '../../constant'
import { BROADSIGN_ACCEPT_CREATION } from './broadSignAcceptCreation'
import { BroadSignCreationModel } from './models/broad-sign-creation.model'
import { BROADSIGN_REJECT_CREATION } from './broadSignRejectCreation'
import { UPDATE_BROADSIGN_CREATION } from './updateBroadsignCreation'
import { ASSIGN_BROADSIGN_CREATION_MEDIA } from './assignBroadsignCreationMedia'
import { SEND_BROADSIGN_CREATION_TO_ACCEPTANCE } from './sendBroadsignCreationToAcceptance'
import { BROADSIGN_CREATION_ACCEPTANCES } from './broadsignCreationAcceptances'
import {
  BroadSignCreationAcceptanceModel,
  BroadSignCreationBuildingUsage,
} from './models/broad-sign-creation-acceptance.model'
import { BroadSignCreationSortColumn } from '../../types/broad-sign'
import { AI_RECOMMENDATION_PROMPT } from './aiRecommendationPrompt'
import { UPDATE_AI_RECOMMENDATION_PROMPT } from './updateAiRecommendationPrompt'
import { AiRecommendationPromptModel } from './models/ai-recommendation-prompt.model'

const broadSignApi = {
  broadSignUpdateCampaignByDeal: ({
    dealId,
    campaignId,
  }: UpdateCampaignByDealModel): Promise<
    FetchResult<{ broadsignUpdateCampaignByDeal: BroadSignUpdateCampaignByDealResponse }>
  > =>
    client.mutate({
      mutation: BROADSIGN_UPDATE_CAMPAIGN_BY_DEAL,
      variables: {
        dealId,
        campaignId,
      },
    }),

  fetchBroadSignCreations: (
    filters: BroadSignFiltersModel,
    first: number
  ): Promise<FetchResult<{ broadsignCreations: BroadSignCreationsResponseModel }>> => {
    const { statuses, mediaFormats, dateFrom, dateTo, text } = filters
    const mappedFilters = {
      statuses,
      mediaFormats,
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
      ...(text && { text }),
    }

    return client.query({
      query: BROADSIGN_CREATIONS,
      variables: {
        filters: mappedFilters,
        first,
        last: BROADSIGN_CREATIONS_PER_PAGE,
        sortColumn: BroadSignCreationSortColumn.USED_BUILDINGS_NOT_SENT_COUNT,
        sortDirection: SortDirection.DESC,
      },
    })
  },

  fetchCreationAcceptances: (
    broadsignCreationId: string
  ): Promise<FetchResult<{ broadsignCreationAcceptances: BroadSignCreationAcceptanceModel[] }>> => {
    return client.query({
      query: BROADSIGN_CREATION_ACCEPTANCES,
      variables: { broadsignCreationId },
      fetchPolicy: 'network-only',
    })
  },

  assignCreationMedia: (
    id: string,
    buildings: BroadSignCreationBuildingUsage[]
  ): Promise<
    FetchResult<{
      assignBroadsignCreationMedia: {
        broadsignCreation: { id: string; usedBuildingsNotSentCount: number }
        errors: string[]
        success: boolean
      }
    }>
  > => {
    return client.mutate({
      mutation: ASSIGN_BROADSIGN_CREATION_MEDIA,
      variables: { id, buildings },
    })
  },

  sendCreationToAcceptance: (
    id: string
  ): Promise<
    FetchResult<{
      sendBroadsignCreationToAcceptance: {
        broadsignCreationBuildings: {
          id: string
          buildingId: string
          acceptanceStatus: string
          used: boolean
        }[]
        errors: string[]
        success: boolean
      }
    }>
  > => {
    return client.mutate({
      mutation: SEND_BROADSIGN_CREATION_TO_ACCEPTANCE,
      variables: { id },
    })
  },

  acceptCreation: (
    id: string
  ): Promise<
    FetchResult<{
      broadsignAcceptCreation: { broadsignCreation: Pick<BroadSignCreationModel, 'id' | 'status'> }
    }>
  > => {
    return client.mutate({
      mutation: BROADSIGN_ACCEPT_CREATION,
      variables: { id },
    })
  },

  rejectCreation: (
    id: string,
    rejectionReason: string
  ): Promise<
    FetchResult<{
      broadsignRejectCreation: { broadsignCreation: Pick<BroadSignCreationModel, 'id' | 'status'> }
    }>
  > => {
    return client.mutate({
      mutation: BROADSIGN_REJECT_CREATION,
      variables: { id, rejectionReason },
    })
  },

  updateBroadsignCreation: (
    id: string,
    commercialSupervisorId?: string,
    departmentId?: string,
    advertiserId?: string
  ): Promise<
    FetchResult<{
      updateBroadsignCreation: {
        broadsignCreation: {
          id: string
          commercialSupervisor: { id: string }
          department: { id: string }
          advertiser: { id: string }
        }
        errors: string[]
      }
    }>
  > => {
    return client.mutate({
      mutation: UPDATE_BROADSIGN_CREATION,
      variables: {
        id,
        // Apollo drops undefined variables, so a cleared select must be sent as null
        commercialSupervisorId: commercialSupervisorId ?? null,
        departmentId: departmentId ?? null,
        advertiserId: advertiserId ?? null,
      },
    })
  },

  getAiRecommendationPrompt: (): Promise<
    FetchResult<{ aiRecommendationPrompt: AiRecommendationPromptModel }>
  > => {
    return client.query({
      query: AI_RECOMMENDATION_PROMPT,
      fetchPolicy: 'network-only',
    })
  },

  updateAiRecommendationPrompt: (
    content: string
  ): Promise<
    FetchResult<{
      updateAiRecommendationPrompt: {
        aiRecommendationPrompt: AiRecommendationPromptModel
        errors: string[]
      }
    }>
  > => {
    return client.mutate({
      mutation: UPDATE_AI_RECOMMENDATION_PROMPT,
      variables: { content },
    })
  },
}

export default broadSignApi
